'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import MaterialIcons from '../MaterialIcons/materialIcons';
import { AskAiIcon, SearchCloseIcon, SearchIcon } from '../Icons/Icons';
import { useSearch } from '@/hooks/useSearch';
import { usePrefetchRecentlyViewed, useRecentlyViewed } from '@/hooks/learner/useRecentlyViewed';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { hasPartnerTag } from '@/lib/constants/partner';
import { sanitizeUserInput } from '@/utils/sanitize-input';
import { containsXss, containsSqlInjection } from '@/utils/validate-input';
import { SEARCH_MIN_LENGTH, SEARCH_MAX_LENGTH, SEARCH_DEBOUNCE_MS } from '@/lib/utils/catalog-constants';
import { useSearchChatCoordination } from '@/utils/search-chat-coordination';
import { deleteQueryParam } from '@/lib/utils/url-params';

interface SearchResult {
  records?: {
    page?: Array<{
      title?: string;
      url?: string;
      entry_uid?: string;
      [key: string]: any;
    }>;
  };
  info?: {
    page?: {
      [key: string]: any;
    };
  };
  [key: string]: any;
}

interface SearchBarNewProps {
  placeholder?: string;
  maxWidth?: string;
  onClose?: () => void;
  autoFocus?: boolean;
  isOpen?: boolean; // For mobile search modal control
  /** When set, clicking/focusing the bar (tab view) opens full-screen search via parent */
  onOpenRequest?: () => void;
}

export const SearchBarNew = ({
  placeholder = 'Search',
  maxWidth = '540px',
  onClose,
  autoFocus = false,
  isOpen = false,
  onOpenRequest,
}: SearchBarNewProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchString, setSearchString] = useState('');
  const [showList, setList] = useState(false);
  const [state, setState] = useState<{ results: SearchResult | null }>({ results: null });
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  
  // Coordination for search-chat behavior
  const coordination = useSearchChatCoordination();
  const [visibleCount, setVisibleCount] = useState(4);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Tab and mobile use full-screen search; desktop only uses inline dropdown
  const DESKTOP_MIN_WIDTH = 1024;

  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount, so
  // the search bar's recently-viewed/partner-content gating never picked up a login/logout
  // that didn't also remount it (it's mounted once in the header for the whole session).
  const { user: authUser, isLoggedIn } = useAuthUser();

  // When search is active (desktop/tablet), lock body scroll and compensate scrollbar width to prevent layout shift/flicker
  useEffect(() => {
    if (isMobile) return;
    if (isSearchActive) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const prevOverflow = document.body.style.overflow;
      const prevPaddingRight = document.body.style.paddingRight;
      const raf = requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
      });
      return () => {
        cancelAnimationFrame(raf);
        document.body.style.overflow = prevOverflow;
        document.body.style.paddingRight = prevPaddingRight;
      };
    }
  }, [isSearchActive, isMobile]);

  // Shown when search is active and empty (stays visible on outside click); server sorts and caps it.
  const userUid = isLoggedIn ? authUser?.uid : undefined;
  const { data: recentlyViewedData = [], isLoading: isLoadingRecentlyViewed } = useRecentlyViewed({
    enabled: Boolean(isLoggedIn) && (isSearchActive || (isOpen && isMobile)) && searchString.length === 0,
    userUid,
    limit: 3,
  });
  // Hovering (desktop) or pressing (mobile trigger) starts the request before the dropdown opens.
  const prefetchRecentlyViewed = usePrefetchRecentlyViewed({ userUid, limit: 3 });

  // Debounce search query (catalog: 400ms)
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(searchString.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchString]);

  // Use React Query hook for search (min 3 chars)
  const { data: searchResults, isLoading } = useSearch({
    query: debouncedQuery,
    enabled: debouncedQuery.length >= SEARCH_MIN_LENGTH && isFocused,
    excludePartnerSpecific: !hasPartnerTag(authUser),
  });

  // Update state when search results change (clear when query changes so we don't show stale results)
  useEffect(() => {
    if (searchResults) {
      setState({ results: searchResults as unknown as SearchResult });
    } else {
      setState({ results: null });
    }
  }, [searchResults, debouncedQuery]);

  const getUrl = () => {
    if (typeof window !== 'undefined') {
      return new URL(window.location.href);
    }
  };

  const applySearchValue = useCallback((value: string): string | null => {
    const sanitized = sanitizeUserInput(value, false);
    if (containsXss(sanitized) || containsSqlInjection(sanitized)) {
      console.warn('Malicious search pattern detected and blocked');
      return null;
    }
    const capped = sanitized.slice(0, SEARCH_MAX_LENGTH);
    setSearchString(capped);
    setVisibleCount(4);
    setList(capped.length > 0);
    return capped;
  }, []);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applySearchValue(e.target.value);
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData('text/plain');
      if (!pasted) return;
      const input = e.currentTarget;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const next = searchString.slice(0, start) + pasted + searchString.slice(end);
      e.preventDefault();
      const applied = applySearchValue(next);
      if (applied !== null) {
        requestAnimationFrame(() => {
          const newPos = Math.min(start + pasted.length, applied.length);
          input.setSelectionRange(newPos, newPos);
        });
      }
    },
    [searchString, applySearchValue]
  );

  // Initialize search string from URL on mount
  useEffect(() => {
    setList(false);
    let url = getUrl();
    let searchString = url?.searchParams.get('q') || '';
    if (searchString) {
      setSearchString(searchString);
    }
  }, []);

  // Auto-focus input when autoFocus prop is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure the element is visible
      setTimeout(() => {
        inputRef.current?.focus();
        setIsFocused(true);
        setIsSearchActive(true);
      }, 100);
    }
  }, [autoFocus]);

  // Set search active when isOpen prop is true (for mobile + tab view)
  useEffect(() => {
    if (isOpen) {
      const useFullScreenView = typeof window !== 'undefined' && window.innerWidth < DESKTOP_MIN_WIDTH;
      if (useFullScreenView) {
        setIsSearchActive(true);
        setIsFocused(true);
        if (inputRef.current) {
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
      } else {
        setIsSearchActive(false);
        setIsFocused(false);
      }
    } else {
      setIsSearchActive(false);
      setIsFocused(false);
    }
  }, [isOpen]);

  // Sync search bar with URL: clear when not on catalog/search; sync q param when on catalog
  const qParam = searchParams.get('q') ?? '';
  useEffect(() => {
    if (!pathname) return;

    const isOnCatalogOrSearch = pathname.includes('/catalog') || pathname.includes('/search');

    if (!isOnCatalogOrSearch) {
      setSearchString('');
      setList(false);
      setIsFocused(false);
      setIsSearchActive(false);
      setState({ results: null });
      return;
    }

    // On catalog: sync with q param - clear search bar when user navigates to catalog without q
    setSearchString(qParam);
    if (!qParam) {
      setDebouncedQuery('');
      setList(false);
      setState({ results: null });
    }
  }, [pathname, qParam]);


  const handleSearchRoute = () => {
    const trimmed = searchString.replace(/\s+/g, ' ').trim();
    if (typeof window !== 'undefined' && trimmed.length >= SEARCH_MIN_LENGTH) {
      const query = trimmed.slice(0, SEARCH_MAX_LENGTH);
      setList(false);
      setIsFocused(false);
      window.location.href = `/academy/catalog?q=${encodeURIComponent(query)}`;
    }
  };

  const handleClear = useCallback(() => {
    // Only clear the searching text, keep recently viewed popup and search active
    setSearchString('');
    setDebouncedQuery('');
    setList(false); // Hide search results, but keep recently viewed
    setState({ results: null });
    setVisibleCount(4);
    deleteQueryParam('q');
    
    // Clear debounce timeout so next keystroke starts a fresh debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    // Keep focus and search state so typing a new keyword immediately triggers search
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleClearAndClose = useCallback(() => {
    // Clear everything and close search so user can start a new search
    setSearchString('');
    setDebouncedQuery('');
    setList(false);
    setIsFocused(false);
    setIsSearchActive(false);
    setState({ results: null });
    setVisibleCount(4);
    deleteQueryParam('q');
    
    // Clear debounce timeout so next search starts fresh
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    // Blur the input
    if (inputRef.current) {
      inputRef.current.blur();
    }
    
    // Notify coordination that search closed
    coordination.onSearchClosed();
    
    // Call onClose callback if provided (for mobile search bar)
    if (onClose) {
      onClose();
    }
  }, [onClose, coordination]);

  const handleCancel = useCallback(() => {
    // Use the clear and close function for cancel button
    handleClearAndClose();
  }, [handleClearAndClose]);


  const handleLoadMore = () => {
    const previousCount = visibleCount;
    setVisibleCount(previousCount + 4);

    setTimeout(() => {
      if (resultsListRef.current) {
        const items = resultsListRef.current.querySelectorAll('a');
        const firstNewItem = items[previousCount];
        if (firstNewItem) {
          firstNewItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  };

  const handleAskAI = () => {
    // Handle Ask AI functionality
  };

  const getBreadcrumb = (url: string, sections?: string[]): string => {
    // Extract breadcrumb from URL path
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      
      // Skip common non-breadcrumb parts
      const skipParts = ['academy', 'www.contentstack.com', 'contentstack.com'];
      const relevantParts = pathParts.filter(part => !skipParts.includes(part.toLowerCase()));
      
      if (relevantParts.length > 0) {
        // Capitalize and format breadcrumb parts
        const breadcrumbParts = relevantParts.slice(0, 2).map(part => {
          // Handle special cases
          if (part === 'learning-paths') return 'Learning Paths';
          if (part === 'courses') return 'Courses';
          if (part === 'content') return 'Content';
          
          // Capitalize words
          return part.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        });
        
        return breadcrumbParts.join(' > ');
      }
    } catch (e) {
      // If URL parsing fails, return empty
    }
    return '';
  };

  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      // Return hostname + pathname, truncate if too long
      const fullUrl = urlObj.hostname + urlObj.pathname;
      return fullUrl.length > 60 ? fullUrl.substring(0, 57) + '...' : fullUrl;
    } catch (e) {
      return url.length > 60 ? url.substring(0, 57) + '...' : url;
    }
  };

  /** In popup results: Course → course icon (article); Video, Video course, Demo, Content (standalone) → video icon (play_circle). */
  const showVideoIcon = (item: { type?: string; tags?: string[]; url?: string; is_standalone?: boolean } | string | undefined) => {
    if (item == null) return false;
    if (typeof item === 'string') {
      const t = item.toLowerCase();
      if (t === 'course' || (t.includes('course') && !t.includes('video'))) return false;
      return t.includes('video') || t.includes('demo') || t.includes('content');
    }
    if ((item as { is_standalone?: boolean }).is_standalone === true) return true;
    const typeStr = item.type?.toLowerCase() || '';
    if (typeStr.includes('course') && !typeStr.includes('video')) return false;
    if (typeStr.includes('video') || typeStr.includes('demo') || typeStr.includes('content')) return true;
    const tags = Array.isArray(item.tags) ? item.tags : [];
    const hasCourseTag = tags.some((tag) => {
      const t = String(tag).toLowerCase();
      return t.includes('course') && !t.includes('video');
    });
    if (hasCourseTag) return false;
    const hasVideoDemoContentTag = tags.some((tag) => {
      const t = String(tag).toLowerCase();
      return t.includes('video') || t.includes('demo') || (t.includes('content') && !t.includes('course'));
    });
    if (hasVideoDemoContentTag) return true;
    const url = (item.url || '').toLowerCase();
    if (url.includes('/video') || url.includes('/demo') || url.includes('/content') || url.includes('video-course') || url.includes('video_course')) return true;
    return false;
  };

  // Mobile + tab: full-screen search; desktop (>= 1024px): inline dropdown
  useEffect(() => {
    const checkView = () => {
      setIsMobile(typeof window !== 'undefined' && window.innerWidth < DESKTOP_MIN_WIDTH);
    };
    checkView();
    window.addEventListener('resize', checkView);
    return () => window.removeEventListener('resize', checkView);
  }, []);

  // Set max-width CSS variable for search bar container (no inline styles)
  useEffect(() => {
    if (containerRef.current && maxWidth) {
      const activeWidth = isSearchActive ? '540px' : maxWidth;
      containerRef.current.style.setProperty('--search-max-width', activeWidth);
    }
  }, [maxWidth, isSearchActive]);

  return (
    <>
      {/* Tab: inline trigger bar in header – click/focus opens full-screen (parent controls isOpen) */}
      {isMobile && onOpenRequest && !isOpen && (
        <div
          role="button"
          tabIndex={0}
          onClick={onOpenRequest}
          onFocus={onOpenRequest}
          onPointerDown={prefetchRecentlyViewed}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onOpenRequest();
            }
            // Do not handle Space so that focus/typing in inputs elsewhere is not affected
          }}
          className="flex w-full items-center rounded-full bg-surface-4 py-3 px-6 cursor-text"
          aria-label="Search"
        >
          <div className="flex items-center gap-2.5 w-full min-w-0">
            <div className="flex shrink-0 items-center justify-center text-text-default-strong">
              <SearchIcon className="h-[18px] w-[18px]" />
            </div>
            <span className="text-sm text-text-default-medium">{placeholder}</span>
          </div>
        </div>
      )}

      {/* Mobile + Tab Full-Screen Search Modal - only when this instance is the modal (not the trigger bar) */}
      {isOpen && isMobile && !onOpenRequest && (typeof window === 'undefined' || window.innerWidth < DESKTOP_MIN_WIDTH) && (
        <>
          {/* Backdrop for mobile - blur on inner layer so backdrop-filter works */}
          <div
            className="fixed inset-0 z-[9998] transition-opacity duration-300 ease-in-out animate-fade-in"
            aria-hidden="true"
            onClick={handleCancel}
          >
            <div className="absolute inset-0 bg-[var(--Surfaces-Overlay-Sheen-75)] backdrop-blur-md" />
          </div>
          
          <div
            className="fixed inset-0 z-[9999] flex flex-col p-4 animate-slide-down overflow-x-hidden overflow-y-auto"
            data-swiftype-index="false"
          >
          {/* Top Bar with Close Button and Search Input */}
          <div className="flex items-center gap-2 pb-4 min-w-0 shrink-0">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => {
                handleCancel();
                coordination.onSearchClosed();
              }}
              className="flex size-[45px] shrink-0 items-center justify-center rounded-full bg-surface-4 hover:bg-surface-3 transition-colors"
              aria-label="Close search"
            >
              <span className="flex items-center justify-center translate-x-0.5">
                <MaterialIcons
                  iconName="arrow_back_ios"
                  className="!text-[18px] text-text-default-strong block"
                />
              </span>
            </button>

            {/* Search Input */}
            <div className="flex-1 relative min-w-0">
              <div className="relative flex w-full items-center rounded-full bg-surface-4 py-3 pr-4 pl-6">
                {/* Search Icon and Input Group - same layout as desktop */}
                <div className="flex items-center gap-2.5 w-full min-w-0">
                  <div className="flex shrink-0 items-center justify-center text-text-default-strong">
                    <SearchIcon className="h-[18px] w-[18px]" />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    className="search-input w-full min-w-0 border-0 bg-transparent text-sm text-text-default-strong placeholder:text-text-default-medium"
                    placeholder={placeholder}
                    value={searchString}
                    onChange={handleOnChange}
                    onPaste={handlePaste}
            onFocus={() => {
              setIsFocused(true);
              setIsSearchActive(true);
              coordination.onSearchOpened();
            }}
                    onKeyDown={(event) => {
                      const enteredString = searchString.replace(/\s+/g, ' ').trim();
                      if (event.key === 'Enter' && enteredString?.length > 0) {
                        handleSearchRoute();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Recently Viewed Section - full width in modal */}
          {isLoggedIn && searchString.length === 0 && (isLoadingRecentlyViewed || recentlyViewedData.length > 0) && (
            <div className="w-full min-w-0 h-auto overflow-hidden text-left shadow-lg flex flex-col search-dropdown-container transition-all duration-300 ease-in-out bg-surface-4 rounded-lg p-8 !mt-0">
              {/* Recently Viewed Header - matches Results (pb-2) */}
              <div className="flex items-center justify-between pb-2">
                <h6 className="cs-h6 text-text-default-strong">Recently viewed</h6>
              </div>

              {/* Loader - matches results row (gap-2) */}
              {isLoadingRecentlyViewed && (
                <ul className="flex flex-col gap-2">
                  {[1, 2, 3].map((i) => (
                    <li key={i} className="flex items-start gap-2 rounded-lg">
                      <div className="flex-shrink-0 amethyst-light-icon p-3">
                        <div className="h-6 w-6 animate-pulse rounded bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent" />
                      </div>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <div className="h-5 w-full animate-pulse rounded-lg bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent mb-1" />
                        <div className="h-3 w-2/5 animate-pulse rounded-lg bg-gradient-to-r from-crystal-tint/40 via-crystal-tint/20 to-transparent" />
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Recently Viewed List - match Results layout (gap-2, cs-h5, cs-caption) */}
              {!isLoadingRecentlyViewed && recentlyViewedData.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {recentlyViewedData.slice(0, 3).map((item: any, index: number) => {
                    const displayUrl = formatUrl(item.href || '');
                    const isVideo = showVideoIcon(item);
                    const iconName = isVideo ? 'play_circle' : 'article';
                    
                    return (
                      <a
                        href={item.href || '#'}
                        className="flex items-start gap-2 transition-colors rounded-lg relative"
                        key={item.uid || index}
                        onClick={handleCancel}
                      >
                        <div className="flex-shrink-0 amethyst-light-icon p-3">
                          <MaterialIcons
                            iconName={iconName}
                            className="!text-[24px] text-amethyst-accent"
                          />
                        </div>
                        <div className="flex-1 min-w-0 py-2 pr-2">
                          <h5 className="cs-h5 text-text-default-strong line-clamp-1">
                            {item.title}
                          </h5>
                          {isVideo && item.duration ? (
                            <div className="cs-caption text-text-default-subtle">
                              Video | {item.duration}
                            </div>
                          ) : displayUrl ? (
                            <div className="cs-caption text-text-default-subtle line-clamp-1 truncate">
                              {displayUrl}
                            </div>
                          ) : null}
                        </div>
                      </a>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Results Section - full width in modal */}
          {showList && searchString.length > 0 && (
            <div className="w-full min-w-0 h-auto overflow-hidden text-left shadow-lg flex flex-col search-dropdown-container transition-all duration-300 ease-in-out bg-surface-4 rounded-lg p-8 !mt-0">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-2">
                <h6 className="cs-h6 text-text-default-strong">Results</h6>
                {/* <button
                  type="button"
                  className="flex items-center gap-2 cs-s-body text-text-default-medium hover:text-text-default-strong transition-colors"
                  aria-label="Filter results"
                  onClick={(e) => {
                    e.preventDefault();
                    // TODO: Implement filter functionality
                  }}
                >
                  <MaterialIcons
                    iconName="tune"
                    className="rotate-90 text-icon-subtle h-5 w-5"
                  />
                  <span>Filter results</span>
                </button> */}
              </div>

              {/* Loader - matches mobile result row (icon + content, no chevron) */}
              {isLoading && (
                <ul className="flex flex-col gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <li key={i} className="flex items-start gap-2 rounded-lg">
                      <div className="flex-shrink-0 amethyst-light-icon py-1.5">
                        <div className="h-6 w-6 animate-pulse rounded bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent" />
                      </div>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <div className="h-5 w-full animate-pulse rounded-lg bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent mb-1" />
                        <div className="h-3 w-2/5 animate-pulse rounded-lg bg-gradient-to-r from-crystal-tint/40 via-crystal-tint/20 to-transparent" />
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Results List - fixed 4-items height, scroll inside; View more stays outside visible */}
              {!isLoading && state.results?.records?.page && state.results.records.page.length > 0 && (
                <>
                  <div ref={resultsListRef} className="search-results-scroll max-h-[300px] overflow-y-auto pr-1">
                    <ul className="flex flex-col gap-2">
                    {state.results?.records?.page?.slice(0, visibleCount).map((searchItem: any, index: number) => {
                      const breadcrumb = getBreadcrumb(searchItem.url || '', searchItem.sections);
                      const displayUrl = formatUrl(searchItem.url || '');
                      const isVideo = showVideoIcon(searchItem);

                      return (
                        <a
                          href={searchItem.url ? encodeURI(searchItem.url) : '#'}
                          className="flex items-start gap-2 transition-colors rounded-lg relative"
                          key={index}
                          onClick={handleCancel}
                        >
                          <div className="flex-shrink-0 amethyst-light-icon py-1.5">
                            <MaterialIcons
                              iconName={isVideo ? 'play_circle' : 'article'}
                              className="!text-[24px]"
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 py-2 pr-2">
                            <h5 className="cs-h5 line-clamp-1">
                              {searchItem.title}
                            </h5>
                            {displayUrl && (
                              <div className="cs-caption text-text-default-subtle line-clamp-1 truncate">
                                {displayUrl}
                              </div>
                            )}
                          </div>
                        </a>
                      );
                    })}
                  </ul>
                  </div>

                  {/* View More Results - outside scroll, always visible */}
                  {state.results?.records?.page && state.results.records.page.length > 4 && visibleCount < state.results.records.page.length && (
                    <button
                      onClick={handleLoadMore}
                      className="w-full text-left cs-s-body text-text-default-light hover:text-text-default-strong transition-colors pt-2"
                    >
                      View more results
                    </button>
                  )}
                </>
              )}

              {/* Empty State */}
              {!isLoading && (!state.results?.records?.page || state.results.records.page.length === 0) && (
                <div className="py-8 text-center">
                  <p className="cs-s-body">No results found</p>
                </div>
              )}
            </div>
          )}
          </div>
        </>
      )}

      {/* Backdrop overlay when search is active - Desktop only; hide when isOpen (modal instance) to avoid flash */}
      {!isMobile && !isOpen && (
      <div
        className={`fixed inset-0 z-[9998] transition-opacity duration-200 ease-out ${
          isSearchActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[var(--Surfaces-Overlay-Sheen-75)] backdrop-blur-md" />
      </div>
      )}
      
      {/* Desktop Search Bar - only when desktop viewport; never when isOpen (modal instance) to avoid flash below menu */}
      {!isMobile && !isOpen && (
      <div className={`w-full flex justify-center ${isSearchActive ? 'fixed top-20 left-1/2 transform -translate-x-1/2 z-[9999]' : ''}`}>
        <div
          className={`relative flex items-center justify-between transition-all duration-200 ease-out ${isSearchActive ? 'w-[540px]' : 'w-[320px]'}`}
          style={{ transformOrigin: 'right center' }}
          data-swiftype-index="false"
          onClick={(e) => {
            // Prevent backdrop click when clicking on search container
            e.stopPropagation();
          }}
        >
        <div 
          ref={containerRef}
          className={`relative flex items-center gap-2 overflow-visible search-bar-width-container w-full transition-all duration-200 ease-out`}
          style={
            isSearchActive 
              ? { width: '540px', maxWidth: '540px', transformOrigin: 'right center' } 
              : { width: '320px', maxWidth: '320px', transformOrigin: 'right center' }
          }
        >
        <div 
          className={`relative flex w-full items-center rounded-full ${isSearchActive ? 'bg-surface-4' : 'bg-on-surface-2-strong'} transition-all duration-200 ease-out py-2.5 pr-4 pl-6`}
          style={
            isSearchActive 
              ? { width: '540px', maxWidth: '540px', transformOrigin: 'right center' } 
              : { width: '320px', maxWidth: '320px', transformOrigin: 'right center' }
          }
        >

        {/* Search Icon and Input Group - pr-10 so text does not go behind the close icon */}
        <div className="flex items-center gap-2.5 w-full min-w-0 pr-6">
          {/* Search Icon */}
          <div className="flex items-center justify-center flex-shrink-0 text-text-default-strong">
            <SearchIcon className="h-[18px] w-[18px]" />
          </div>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            className="search-input w-full min-w-0 border-0 text-sm text-text-default-strong"
            placeholder={placeholder}
            onPointerEnter={prefetchRecentlyViewed}
            value={searchString}
            onChange={handleOnChange}
            onPaste={handlePaste}
            onFocus={() => {
              setIsFocused(true);
              setIsSearchActive(true);
              coordination.onSearchOpened();
            }}
            onBlur={(e) => {
              // Don't blur if clicking on dropdown items or cancel button
              const relatedTarget = e.relatedTarget as HTMLElement;
              if (!relatedTarget || !relatedTarget.closest('.search-dropdown-container')) {
                // Delay blur to allow dropdown clicks
                setTimeout(() => {
                  if (!document.activeElement?.closest('.search-dropdown-container')) {
                    setIsFocused(false);
                  }
                }, 200);
              }
            }}
            onKeyDown={(event) => {
              const enteredString = searchString.replace(/\s+/g, ' ').trim();
              if (event.key === 'Enter' && enteredString?.length > 0) {
                handleSearchRoute();
              }
              if (
                event.keyCode === 40 &&
                showList &&
                state.results?.records?.page &&
                state.results.records.page.length > 0
              ) {
                event.preventDefault();
                const firstSuggestion = document.querySelector('.suggestions-list a') as HTMLElement;
                if (firstSuggestion) {
                  firstSuggestion.focus();
                }
              }
            }}
          />
        </div>
        
        {/* Separator and Close Icon Group */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2.5">
          {/* Keyboard shortcut - hidden when search opened */}
          {/* {!isSearchActive && (
            <div className="flex h-6 w-6 items-center justify-center rounded-[6px] border border-border-light bg-on-surface-1-medium opacity-100">
              <span className="text-text-default-subtle">/</span>
            </div>
          )} */}

          {/* Close Icon - Clear and close search so user can start a new search */}
          <button
            type="button"
            aria-label="Clear and close search"
            onClick={handleClearAndClose}
            disabled={searchString.length === 0}
            className={`flex items-center justify-center rounded transition-colors w-6 h-6 flex-shrink-0 ${
              searchString.length > 0
                ? 'cursor-pointer text-icon-strong hover:bg-on-surface-3 hover:text-text-default-strong opacity-100'
                : 'cursor-default text-icon-strong opacity-100 pointer-events-none'
            }`}
          >
            <SearchCloseIcon
              active={searchString.length > 0}
              className="h-[10px] w-[10px] flex-shrink-0 text-current"
            />
          </button>
        </div>
      </div>
      </div>

        {/* Cancel button - at full right end of row; gap-3 on parent keeps it clear of search box */}
        {isSearchActive && (
          <button
            type="button"
            onClick={handleCancel}
            className="cursor-pointer whitespace-nowrap px-4 py-2 flex-shrink-0 relative z-[10002] cs-s-body-regular text-text-default-strong transition-colors"
            aria-label="Cancel search"
          >
            Cancel
          </button>
        )}

        {/* Ask AI Button - Outside search bar */}
        {/* <button
          type="button"
          onClick={handleAskAI}
          className={`flex items-center gap-1.5 rounded-full bg-surface-4 px-6 py-2.5 text-xs font-medium text-text-default-light transition-colors hover:bg-amethyst-light whitespace-nowrap ${isSearchActive ? 'mr-20' : ''}`}
          aria-label="Ask AI"
        >
          <span className="cs-s-body text-text-default-light">Ask AI</span>
          <div className="text-icon-subtle"> <AskAiIcon width={18} height={18} /></div>
        </button> */}

        {/* Recently Viewed Dropdown - show when search active and empty (stays when user clicks outside input) */}
        {isLoggedIn && isSearchActive && searchString.length === 0 && (isLoadingRecentlyViewed || recentlyViewedData.length > 0) && (
          <div
            className="absolute top-full left-0 mt-2 z-[10001] w-full overflow-hidden text-left shadow-lg flex flex-col search-dropdown-container transition-all duration-200 ease-out bg-surface-4 rounded-lg p-8"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {/* Recently Viewed Header - matches Results (pb-2) */}
            <div className="flex items-center justify-between pb-2">
              <h6 className="cs-h6 text-text-default-strong">Recently viewed</h6>
            </div>

            {/* Loader - matches results row (gap-2, icon p-3) */}
            {isLoadingRecentlyViewed && (
              <ul className="flex flex-col gap-2">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg">
                    <div className="flex-shrink-0 amethyst-light-icon p-3">
                      <div className="h-6 w-6 animate-pulse rounded bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent" />
                    </div>
                    <div className="flex-1 min-w-0 py-2 pr-2">
                      <div className="h-5 w-full animate-pulse rounded-lg bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent mb-1" />
                      <div className="h-3 w-2/5 animate-pulse rounded-lg bg-gradient-to-r from-crystal-tint/40 via-crystal-tint/20 to-transparent" />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Recently Viewed List - match Results (gap-2, cs-h5, cs-caption, chevron on hover) */}
            {!isLoadingRecentlyViewed && recentlyViewedData.length > 0 && (
              <ul className="flex flex-col gap-2">
                {recentlyViewedData.slice(0, 3).map((item: any, index: number) => {
                  const displayUrl = formatUrl(item.href || '');
                  const isVideo = showVideoIcon(item);
                  const iconName = isVideo ? 'play_circle' : 'article';
                  
                  return (
                    <a
                      href={item.href || '#'}
                      className="group flex items-start gap-2 transition-colors rounded-lg search-result-item-hover relative"
                      key={item.uid || index}
                    >
                      <div className="flex-shrink-0 amethyst-light-icon p-3">
                        <MaterialIcons
                          iconName={iconName}
                          className="!text-[24px] text-amethyst-accent"
                        />
                      </div>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <h5 className="cs-h5 text-text-default-strong line-clamp-1">
                          {item.title}
                        </h5>
                        {isVideo && item.duration ? (
                          <div className="cs-caption text-text-default-subtle">
                            Video | {item.duration}
                          </div>
                        ) : displayUrl ? (
                          <div className="cs-caption text-text-default-subtle line-clamp-1 truncate">
                            {displayUrl}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex-shrink-0 p-2 opacity-0 transition-opacity duration-200 flex items-center justify-center self-center group-hover:opacity-100">
                        <MaterialIcons
                          iconName="chevron_right"
                          className="!text-[24px] text-text-default-subtle"
                        />
                      </div>
                    </a>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Suggestions Dropdown */}
        {showList && searchString.length > 0 && (
          <div
            className="absolute top-full left-0 mt-2 z-[10001] w-full overflow-hidden text-left shadow-lg flex flex-col search-dropdown-container transition-all duration-200 ease-out bg-surface-4 rounded-lg p-8"
            onMouseDown={(e) => {
              // Prevent input blur when clicking on dropdown
              e.preventDefault();
            }}
          >
          {/* Header */}
          <div className="flex items-center justify-between pb-2">
            <h6 className="cs-h6">Results</h6>
            {/* <button
              type="button"
              className="flex items-center gap-2 additional-type-s-button"
              aria-label="Filter results"
            >
              <MaterialIcons
                iconName="tune"
                className="rotate-90 text-icon-subtle filter-icon-size"
              />
              <span className="additional-type-s-button">Filter results</span>
            </button> */}
          </div>

          {/* Loader - matches desktop result row (gap-2, icon p-3, title + breadcrumb + url) */}
          {isLoading && (
            <ul className="flex flex-col gap-2 overflow-y-auto">
              {[1, 2, 3, 4].map((i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg">
                  <div className="flex-shrink-0 amethyst-light-icon p-3">
                    <div className="h-6 w-6 animate-pulse rounded bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent" />
                  </div>
                  <div className="flex-1 min-w-0 py-2 pr-2">
                    <div className="h-5 w-full animate-pulse rounded-lg bg-gradient-to-r from-amethyst-light/60 via-amethyst-light/40 to-transparent mb-1" />
                    <div className="h-3 w-3/5 animate-pulse rounded-lg bg-gradient-to-r from-crystal-tint/50 via-crystal-tint/30 to-transparent mb-1" />
                    <div className="h-3 w-2/5 animate-pulse rounded-lg bg-gradient-to-r from-crystal-tint/40 via-crystal-tint/20 to-transparent" />
                  </div>
                  <div className="flex-shrink-0 w-10 h-10 animate-pulse rounded bg-gradient-to-r from-crystal-tint/30 to-transparent" />
                </li>
              ))}
            </ul>
          )}

          {/* Results List - fixed 4-items height, scroll inside; View more stays outside visible */}
          {!isLoading && state.results?.records?.page && state.results.records.page.length > 0 && (
            <>
              <div
                ref={resultsListRef}
                className="search-results-scroll max-h-[300px] overflow-y-auto pr-1"
              >
                <ul className="flex flex-col gap-2">
                {state.results?.records?.page?.slice(0, visibleCount).map((searchItem: any, index: number) => {
                  const breadcrumb = getBreadcrumb(searchItem.url || '', searchItem.sections);
                  const displayUrl = formatUrl(searchItem.url || '');
                  const isVideo = showVideoIcon(searchItem);

                  return (
                    <a
                      href={searchItem.url ? encodeURI(searchItem.url) : '#'}
                      className="group flex items-start gap-2 transition-colors rounded-lg search-result-item-hover relative"
                      key={index}
                    >
                      <div className="flex-shrink-0 amethyst-light-icon p-3">
                        <MaterialIcons
                          iconName={isVideo ? 'play_circle' : 'article'}
                          className="!text-[24px]"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        {/* Title */}
                        <h5 className="cs-h5 line-clamp-1">
                          {searchItem.title}
                        </h5>
                        
                        {/* Breadcrumb */}
                        {breadcrumb && (
                          <div className="cs-caption line-clamp-1">
                            {breadcrumb}
                          </div>
                        )}
                        
                        {/* URL */}
                        {displayUrl && (
                          <div className="cs-caption text-text-default-subtle line-clamp-1 truncate">
                            {displayUrl}
                          </div>
                        )}
                      </div>

                      {/* Chevron - shown on hover */}
                      <div className="flex-shrink-0 p-2 opacity-0 transition-opacity duration-200 flex items-center justify-center self-center group-hover:opacity-100">
                        <MaterialIcons
                          iconName="chevron_right"
                          className="!text-[24px] text-text-default-subtle"
                        />
                      </div>
                    </a>
                  );
                })}
              </ul>
              </div>

              {/* View More Results - outside scroll, always visible */}
              {state.results?.records?.page && state.results.records.page.length > 4 && visibleCount < state.results.records.page.length && (
                <button
                  onClick={handleLoadMore}
                  className="block w-full transition-colors hover:opacity-80 text-left view-more-results-button additional-type-s-button pt-2"
                >
                  View more results
                </button>
              )}
            </>
          )}

          {/* Empty State */}
          {!isLoading && (!state.results?.records?.page || state.results.records.page.length === 0) && (
            <div className="py-8 text-center">
              <p className="cs-s-body">No results found</p>
            </div>
          )}
        </div>
        )}
      </div>
      </div>
      )}
    </>
  );
};